import { useContext } from 'react'
import { useIntl } from 'react-intl'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import {
  INTL_DMY_CASUAL_FORMAT,
  INTL_DAY_MONTH_NAME,
} from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { extractOfferIdsFromOrder } from 'tripPlanner/components/EntryPoints/utils'
import BookingItemCard from 'tripPlanner/components/ItemCard/BookingItemCard'
import { useFlightDetailsForItem } from 'tripPlanner/hooks/api/flights'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import useOffers from 'hooks/Offers/useOffers'

type Props = {
  orderFlightItem: App.OrderFlightItem
  showBorder?: boolean
  order: App.Order
  onSelect?: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function FlightBookingCard(props: Props) {
  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const intlDateFormatter = useIntlDateFormatter()
  const { orderFlightItem, showBorder, order, onSelect } = props
  const { orderId, id } = orderFlightItem

  const [offers, isOffersLoading] = useOffers(extractOfferIdsFromOrder(order))
  const { data: flightDetails, isLoading: isFlightDetailsLoading } =
    useFlightDetailsForItem({ orderId, itemId: id })

  const { fares } = flightDetails?.journeyFares || {}

  const [departing, returning] = fares ?? []
  const departingFlights = departing?.flightGroup.flights ?? []
  const returningFlights = returning?.flightGroup.flights ?? []

  const departingFlight = departingFlights[0]

  const arrivalFlight = departingFlights[departingFlights.length - 1]
  const returningArrivalFlight = returningFlights[returningFlights.length - 1]

  const stripCountryFromAirportName = (
    airportName?: string,
    country?: string,
  ) => {
    if (!airportName || !country) {
      return airportName || ''
    }

    const countrySuffix = `, ${country}`
    if (airportName.endsWith(countrySuffix)) {
      return airportName.slice(0, -countrySuffix.length)
    }

    return airportName
  }

  const formatAirportLabel = (
    airportName?: string,
    country?: string,
    airportCode?: string,
  ) => {
    const normalizedAirportName = stripCountryFromAirportName(
      airportName,
      country,
    )
    if (normalizedAirportName && airportCode) {
      return intl.formatMessage(
        {
          id: 'K_0UIKDgO8_G1wy0Nb3x',
          defaultMessage: '{airportName} ({airportCode})',
          description: 'Flight booking card airport label with airport name and code in brackets',
        },
        {
          airportName: normalizedAirportName,
          airportCode,
        },
      )
    }

    return normalizedAirportName || airportCode || ''
  }

  const departureFlightLabel = formatAirportLabel(
    departingFlight?.departureAirportName,
    departingFlight?.departureCountry,
    departingFlight?.departureAirport,
  )
  const arrivalFlightLabel = formatAirportLabel(
    arrivalFlight?.arrivalAirportName,
    arrivalFlight?.arrivalCountry,
    arrivalFlight?.arrivalAirport,
  )

  const isFetching = isOffersLoading || isFlightDetailsLoading

  const renderStartAndEndDates = () => {
    let startAndEndDates = ''
    if (departingFlight?.departingDate) {
      startAndEndDates += intlDateFormatter(
        departingFlight.departingDate,
        INTL_DAY_MONTH_NAME,
      )
    }

    if (returningArrivalFlight?.arrivalDate) {
      startAndEndDates += ` - ${intlDateFormatter(
        returningArrivalFlight.arrivalDate,
        INTL_DMY_CASUAL_FORMAT,
      )}`
    }

    return startAndEndDates
  }

  if (isFetching || !departingFlight || !arrivalFlight) {
    return <LoadingIndicator inline />
  }

  return (
    <BookingItemCard
      heading={intl.formatMessage(
        {
          id: 'sX3v8hpMVl8Q8uZaQu50',
          defaultMessage: '{departureFlightLabel} → {arrivalFlightLabel}',
          description: 'Flight booking card heading showing origin and destination labels',
        },
        {
          departureFlightLabel,
          arrivalFlightLabel,
        },
      )}
      subHeading={renderStartAndEndDates()}
      price={formatDynamicAmountWithDollarType(
        orderFlightItem.total,
        currentCurrency,
        currentRegionCode,
      )}
      showBorder={showBorder}
      imgUrl={departing?.carrierLogo}
      imgStyle="logo"
      onSelect={(checked) => {
        if (onSelect) {
          onSelect(order, offers, checked)
        }
      }}
    />
  )
}

export default FlightBookingCard
